// src/pages/OcfHub.jsx
import React, { useContext } from "react";
import { Link, Navigate } from "react-router-dom";
import "./OcfHub.css";
import Seo from "../components/Seo";
import YouTubeEmbed from "../components/YouTubeEmbed";
import { LangContext } from "../lang/LangProvider";
import { trackVideoPlay } from "../hooks/useAnalytics";
import { withUtm } from "../utils/utm";
import { OCF_PLAYLIST, OCF_SUBJECTS } from "../data/ocfEpisodes";

function L(lang, it, en) {
  return lang === "en" ? en : it;
}

// Episodio "futuro" se ha availableFrom nel futuro rispetto al build/now.
function futureLabel(ep, lang) {
  if (!ep.availableFrom) return null;
  const d = new Date(ep.availableFrom);
  if (isNaN(d) || d <= new Date()) return null;
  const fmt = d.toLocaleDateString(lang === "en" ? "en-GB" : "it-IT", {
    day: "numeric", month: "long", year: "numeric",
  });
  return L(lang, `dal ${fmt}`, `from ${fmt}`);
}

export default function OcfHub() {
  const { lang } = useContext(LangContext);

  // OCF è un ente esclusivamente italiano: nessuna versione EN, redirect a /it/ocf.
  if (lang !== "it") return <Navigate to="/it/ocf" replace />;

  const seoTitle = L(lang, "Hub Esame OCF — ripasso per materia", "OCF Exam Hub — study by subject");
  const seoDesc = L(
    lang,
    "Hub di ripasso per l'Esame OCF: playlist completa per materia, guida e strumenti. Contenuto educativo, non consulenza.",
    "Study hub for the Italian OCF exam: full playlist by subject, guide and tools. Educational content, not advice."
  );

  const guideTo = `/${lang}/kb/guida-completa-esame-ocf-consulenti-finanziari`;
  const toolsHref = withUtm(`/${lang}/tools`, { source: "site", medium: "article", campaign: "ocf-hub" });

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={`/${lang}/ocf`}
        image="/og/cover-tools.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          name: seoTitle,
          description: seoDesc,
          provider: { "@type": "Organization", name: "AlphaFrame Analytics" },
        }}
      />

      <div className="ocf-hub">
        <p className="ocf-eyebrow">
          <a href={`/${lang}/series`}>← {L(lang, "Le Serie", "Series")}</a>
        </p>
        <header className="ocf-hub-head">
          <h1>{L(lang, "Hub di ripasso per l'Esame OCF", "OCF Exam study hub")}</h1>
          <p className="ocf-hub-intro">
            {L(
              lang,
              "Hub di ripasso per l'Esame OCF. Contenuto educativo, non consulenza personalizzata né raccomandazione.",
              "Study hub for the Italian OCF exam. Educational content, not personalized advice or a recommendation."
            )}
          </p>
        </header>

        <YouTubeEmbed
          playlistId={OCF_PLAYLIST}
          title={L(lang, "Playlist completa Esame OCF", "Full OCF Exam playlist")}
          onPlay={(id) => trackVideoPlay(id, "ocf-hub")}
        />

        <section className="ocf-hub-index" aria-label={L(lang, "Indice per materia", "Index by subject")}>
          <h2>{L(lang, "Indice per materia", "Index by subject")}</h2>
          <div className="ocf-subjects">
            {OCF_SUBJECTS.map((s) => (
              <div className="ocf-subject" key={s.path}>
                <h3>{L(lang, s.titleIt, s.titleEn)}</h3>
                <ul>
                  {s.episodes.map((ep) => {
                    const title = L(lang, ep.titleIt, ep.titleEn);
                    const badge = futureLabel(ep, lang);
                    return (
                      <li key={ep.n}>
                        {ep.slug ? (
                          <Link to={`/${lang}/ocf/${s.path}/${ep.slug}`}>
                            <span className="ocf-ep-n">Ep {ep.n}</span> {title}
                          </Link>
                        ) : (
                          <span>
                            <span className="ocf-ep-n">Ep {ep.n}</span> {title}
                          </span>
                        )}
                        {badge && <span className="ocf-badge">{badge}</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="ocf-hub-links">
          <p>
            {L(lang, "Approfondisci nella ", "Read more in the ")}
            <Link to={guideTo}>
              {L(lang, "guida completa all'Esame OCF", "complete OCF exam guide")}
            </Link>
            {L(lang, " oppure prova i ", " or try the ")}
            <a href={toolsHref}>{L(lang, "strumenti gratuiti", "free tools")}</a>.
          </p>
        </section>

        <p className="ocf-hub-disclaimer">
          {L(
            lang,
            "Contenuto a scopo esclusivamente educativo. Non costituisce consulenza personalizzata né raccomandazione.",
            "Educational content only. Not personalized advice or a recommendation."
          )}
        </p>
      </div>
    </>
  );
}
